import React, { useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import { TankState } from '@tankworld/shared';

interface TankModelProps {
  tank: TankState;
  isSelf: boolean;
}

export const TankModel: React.FC<TankModelProps> = ({ tank, isSelf }) => {
  const rootRef = useRef<THREE.Group>(null);
  const turretRef = useRef<THREE.Group>(null);
  const barrelRef = useRef<THREE.Group>(null);
  const leftTracksRef = useRef<THREE.Group>(null);
  const rightTracksRef = useRef<THREE.Group>(null);

  // Recoil animation state
  const recoilOffset = useRef(0);

  useFrame((_, delta) => {
    if (!rootRef.current) return;

    // Smoothly position tank
    rootRef.current.position.set(tank.position.x, tank.position.y, tank.position.z);

    // Apply pitch and roll to hull
    rootRef.current.rotation.set(tank.pitch, tank.hullYaw, tank.roll, 'YXZ');

    // Turret orientation in world yaw relative to hull
    if (turretRef.current) {
      // Relative yaw between turret world yaw and hull yaw
      turretRef.current.rotation.y = tank.turretYaw - tank.hullYaw;
    }

    // Gun elevation
    if (barrelRef.current) {
      barrelRef.current.rotation.x = tank.gunPitch;

      // Handle recoil recovery
      if (recoilOffset.current > 0.001) {
        recoilOffset.current = THREE.MathUtils.lerp(recoilOffset.current, 0, delta * 12.0);
        barrelRef.current.position.z = -recoilOffset.current;
      }
    }
  });

  const baseColor = tank.isAlive ? tank.color : '#252525';
  const trackColor = '#1c1e1c';
  const armorSteel = '#3a4038';

  return (
    <group ref={rootRef}>
      {/* 1. HULL CHASSIS */}
      <group position={[0, 0.7, 0]}>
        {/* Main lower hull */}
        <mesh castShadow receiveShadow>
          <boxGeometry args={[3.2, 0.9, 6.2]} />
          <meshStandardMaterial color={baseColor} roughness={0.7} metalness={0.2} />
        </mesh>

        {/* Sloped front glacis plate */}
        <mesh position={[0, 0.3, 2.9]} rotation={[-0.45, 0, 0]} castShadow>
          <boxGeometry args={[3.0, 0.4, 1.4]} />
          <meshStandardMaterial color={baseColor} roughness={0.7} metalness={0.2} />
        </mesh>

        {/* Engine deck exhaust grilles */}
        <mesh position={[0, 0.46, -2.1]} castShadow>
          <boxGeometry args={[2.4, 0.1, 1.6]} />
          <meshStandardMaterial color="#1a1a1a" roughness={0.9} />
        </mesh>

        {/* Side armor skirts (spaced armor) */}
        <mesh position={[-1.65, -0.1, 0]} castShadow>
          <boxGeometry args={[0.1, 0.7, 5.8]} />
          <meshStandardMaterial color={armorSteel} roughness={0.8} />
        </mesh>
        <mesh position={[1.65, -0.1, 0]} castShadow>
          <boxGeometry args={[0.1, 0.7, 5.8]} />
          <meshStandardMaterial color={armorSteel} roughness={0.8} />
        </mesh>

        {/* Mud flaps */}
        <mesh position={[0, -0.2, 3.15]} castShadow>
          <boxGeometry args={[3.3, 0.3, 0.1]} />
          <meshStandardMaterial color="#222" roughness={0.9} />
        </mesh>
      </group>

      {/* 2. TRACKS & ROAD WHEELS */}
      <group position={[0, 0.35, 0]}>
        {/* Left Track & Wheels */}
        <group ref={leftTracksRef} position={[-1.4, 0, 0]}>
          <mesh castShadow>
            <boxGeometry args={[0.55, 0.65, 6.0]} />
            <meshStandardMaterial color={trackColor} roughness={0.95} />
          </mesh>
          {/* Road wheels */}
          {[-2.2, -1.1, 0, 1.1, 2.2].map((zPos, idx) => (
            <mesh key={idx} position={[0, -0.05, zPos]} rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.34, 0.34, 0.52, 16]} />
              <meshStandardMaterial color="#303530" roughness={0.8} />
            </mesh>
          ))}
        </group>

        {/* Right Track & Wheels */}
        <group ref={rightTracksRef} position={[1.4, 0, 0]}>
          <mesh castShadow>
            <boxGeometry args={[0.55, 0.65, 6.0]} />
            <meshStandardMaterial color={trackColor} roughness={0.95} />
          </mesh>
          {[-2.2, -1.1, 0, 1.1, 2.2].map((zPos, idx) => (
            <mesh key={idx} position={[0, -0.05, zPos]} rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.34, 0.34, 0.52, 16]} />
              <meshStandardMaterial color="#303530" roughness={0.8} />
            </mesh>
          ))}
        </group>
      </group>

      {/* 3. TURRET ASSEMBLY (Rotates independently) */}
      <group ref={turretRef} position={[0, 1.4, 0.2]}>
        {/* Turret main body with low-profile angular dome */}
        <mesh castShadow receiveShadow position={[0, 0.35, -0.2]}>
          <cylinderGeometry args={[1.5, 1.85, 0.8, 12]} />
          <meshStandardMaterial color={baseColor} roughness={0.65} metalness={0.25} />
        </mesh>

        {/* Commander cupola and periscopes */}
        <mesh position={[-0.6, 0.8, -0.3]} castShadow>
          <cylinderGeometry args={[0.35, 0.38, 0.25, 12]} />
          <meshStandardMaterial color="#2d302c" roughness={0.7} />
        </mesh>

        {/* Gun mantlet */}
        <mesh position={[0, 0.35, 1.4]} castShadow>
          <boxGeometry args={[1.3, 0.65, 0.7]} />
          <meshStandardMaterial color={armorSteel} roughness={0.6} />
        </mesh>

        {/* 4. GUN BARREL (Elevates independently & recoils) */}
        <group ref={barrelRef} position={[0, 0.35, 1.6]}>
          {/* Main barrel tube */}
          <mesh position={[0, 0, 2.2]} rotation={[Math.PI / 2, 0, 0]} castShadow>
            <cylinderGeometry args={[0.11, 0.14, 4.4, 16]} />
            <meshStandardMaterial color="#2c3029" roughness={0.5} metalness={0.3} />
          </mesh>

          {/* Bore evacuator cylinder in the middle of barrel */}
          <mesh position={[0, 0, 2.4]} rotation={[Math.PI / 2, 0, 0]} castShadow>
            <cylinderGeometry args={[0.18, 0.18, 0.85, 16]} />
            <meshStandardMaterial color="#222520" roughness={0.6} />
          </mesh>

          {/* Muzzle brake at barrel tip */}
          <mesh position={[0, 0, 4.35]} rotation={[Math.PI / 2, 0, 0]} castShadow>
            <cylinderGeometry args={[0.16, 0.16, 0.35, 16]} />
            <meshStandardMaterial color="#1a1a1a" roughness={0.8} />
          </mesh>
        </group>
      </group>

      {/* 5. NAMEPLATE & HEALTH OVERHEAD (For opponents / bots) */}
      {!isSelf && tank.isAlive && (
        <group position={[0, 3.2, 0]}>
          <Text
            position={[0, 0.45, 0]}
            fontSize={0.45}
            color="#ffffff"
            anchorX="center"
            anchorY="middle"
          >
            {tank.name}
          </Text>
          {/* Health bar background */}
          <mesh position={[0, 0, 0]}>
            <planeGeometry args={[2.0, 0.22]} />
            <meshBasicMaterial color="#222222" />
          </mesh>
          {/* Health bar foreground */}
          <mesh position={[(tank.health / tank.maxHealth - 1) * 0.98, 0, 0.01]}>
            <planeGeometry args={[(tank.health / tank.maxHealth) * 1.96, 0.18]} />
            <meshBasicMaterial color={tank.health > 40 ? '#4ade80' : '#ef4444'} />
          </mesh>
        </group>
      )}
    </group>
  );
};
